package com.example.mystybag.core.logic.base

import androidx.compose.material3.SnackbarDuration
import androidx.compose.ui.text.input.ImeAction
import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.slot.SlotNavigation
import com.arkivanov.decompose.router.slot.activate
import com.arkivanov.decompose.router.slot.dismiss
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.stack.replaceAll
import com.arkivanov.decompose.router.stack.replaceCurrent
import com.arkivanov.decompose.value.MutableValue
import com.arkivanov.decompose.value.Value
import com.arkivanov.decompose.value.update
import com.arkivanov.essenty.backhandler.BackCallback
import com.arkivanov.essenty.lifecycle.Lifecycle
import dev.icerock.moko.resources.StringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.defines.UtilsActionsDefines.ACTION_NONE
import com.example.mystybag.core.expected.LogHelper.logEvent
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.logException
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.ExperimentalMystyBagApi
import com.example.mystybag.core.extensions.LifecycleThreshold
import com.example.mystybag.core.extensions.deleteFirst
import com.example.mystybag.core.extensions.generateRandomString
import com.example.mystybag.core.extensions.ifNullOrEmpty
import com.example.mystybag.core.extensions.safeCast
import com.example.mystybag.core.extensions.withLifecycle
import com.example.mystybag.core.helpers.ProgressListener
import com.example.mystybag.core.logic.helpers.DialogResult
import com.example.mystybag.core.logic.helpers.UIBottomSheetControl
import com.example.mystybag.core.model.BaseUIState
import com.example.mystybag.core.model.SnackbarConfig
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_EXCEPTION
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_NO_INTERNET_CONNECTION
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineName
import kotlinx.coroutines.CoroutineScope
import com.example.mystybag.core.expected.CoreDispatchers
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancelChildren
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.channels.ReceiveChannel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.CoroutineContext
import kotlin.coroutines.cancellation.CancellationException

abstract class BaseViewModel(
    private val context                 : AppContext,
    val baseAction                      : Int           = ACTION_NONE
): ParentCallback,
    ComponentContext by context,
    UIBottomSheetControl {

    private     var semaphore               : Semaphore                             = Semaphore(1)
    private     val backButtonHandler       = BackCallback(isEnabled = false)       { onBackButtonClicked() }
    private     var startTime               : Long                                  = 0
    private     var activityPending         : Boolean                               = true

    private     val mutex                   = Mutex()
    private     val sheetNavigation         : SlotNavigation<UIBottomSheetConfig>   = SlotNavigation()
    private     val pendingBottomSheetQueue : MutableList<UIBottomSheetConfig>      = mutableListOf()
    private     var bottomSheetCloseAction  : ((BottomSheetChild?) -> Unit)?        = null

    protected   val scope                   : ViewModelCoroutineScope               = ViewModelCoroutineScope(CoreDispatchers.default, this::class.simpleName?.plus(".IO"))
    protected   val uiScope                 : ViewModelCoroutineScope               = ViewModelCoroutineScope(Dispatchers.Main, this::class.simpleName?.plus(".Main"))

    private     val baseState               : MutableValue<BaseUIState>             = MutableValue(BaseUIState())
    protected   val baseStateData           : BaseUIState
        get() = baseUIState.value

    val parentCallbacks: ParentCallback? = context.callbacks

    override val sheetState = context.bsChildSlot(
        source           = sheetNavigation,
        key              = "BottomSheetSlot@".plus(generateRandomString()),
        handleBackButton = false,
        navigation       = ::bottomSheetNavigation,
        callback         = ::bottomSheetCallback,
        childFactory     = ::configureBottomSheets
    )

    /** Should be used only in views!
     * To get the current state in ViewModels, use baseStateData! */
    override val baseUIState: Value<BaseUIState> = baseState

    protected val currentlyOpenBottomSheet: BottomSheetChild?
        get() = sheetState.value.child?.instance

    /** By default the hardware back button is handled in the Decompose library, it automatically pops the topmost screen, thus the onBackButtonClicked method is NOT called.
     *  Setting this flag to true will prevent this behaviour, the onBackButtonClicked method will be called and it is your responsibility to call super.onBackButtonClicked or super.goBack to exit the screen. */
    protected   var overrideBackButton      : Boolean
        get()      = backButtonHandler.isEnabled
        set(value) { backButtonHandler.isEnabled = value }

    private val lifecycleCallbacks = object: Lifecycle.Callbacks {

        override fun onCreate() {
            backHandler.register(backButtonHandler)
            this@BaseViewModel.onCreate()
        }

        override fun onDestroy() {
            backHandler.unregister(backButtonHandler)
            scope.cancel()
            uiScope.cancel()
            this@BaseViewModel.onDestroy()
        }

        override fun onPause () {
            onVisibilityChanged(false)
            uiScope.launch {
                semaphore.acquire()
            }
            logEvent("Pause VM ${this@BaseViewModel::class.simpleName}")
            this@BaseViewModel.onPause()
        }

        override fun onResume() {
            onVisibilityChanged(true)
            doOnResume()
            logEvent("Resume VM ${this@BaseViewModel::class.simpleName}")
            this@BaseViewModel.onResume()
        }

        override fun onStart() {
            this@BaseViewModel.onStart()
        }

        override fun onStop() {
            this@BaseViewModel.onStop()
        }
    }

    init {
        context.lifecycle.subscribe(lifecycleCallbacks)
    }

    suspend fun showDialog(config: DialogConfig): DialogResult = AppViewModel.displayDialog(config)

    protected val progressListener = context.progressListener ?: object : ProgressListener {

        override fun setProgress(show: Boolean) {
            launchOnMain {
                updateBaseState {
                    it.copy(showProgress = show)
                }
            }
        }
    }

    override fun goBack(onComplete: ((Boolean) -> Unit)?) {
        setResult<ParentCallback> { goBack(onComplete) }
    }

    override fun parentCloseBottomSheet() {
        setResult<ParentCallback> { parentCloseBottomSheet() }
    }

    override fun parentOpenBottomSheet(config: UIBottomSheetConfig) {
        setResult<ParentCallback> { parentOpenBottomSheet(config) }
    }

    open fun navigate(config: NavigatorConfig) {
        context.navigation?.pushNew(config)
    }

    open fun replaceLast(config: NavigatorConfig) {
        context.navigation?.replaceCurrent(config)
    }

    open fun pop() {
        context.navigation?.pop { }
    }

    open fun replaceAll(config: NavigatorConfig) {
        context.navigation?.replaceAll(config)
    }

    protected fun showSnackbar(message: StringResource, duration: SnackbarDuration = SnackbarDuration.Short) = AppViewModel.showSnackbar(
        SnackbarConfig(
            message  = sharedString(message),
            duration = duration
        )
    )

    fun showSnackbar(message: String, duration: SnackbarDuration = SnackbarDuration.Short) = AppViewModel.showSnackbar(
        SnackbarConfig(
            message  = message,
            duration = duration
        )
    )

    protected fun launchOnMain(block: suspend CoroutineScope.() -> Unit) = uiScope.launch(block = block)

    protected inline fun <T> T.doOnMain(crossinline block: suspend CoroutineScope.(T) -> Unit) {
        uiScope.launch {
            block(this, this@doOnMain)
        }
    }

    protected fun uiThread(functionToExecute: suspend CoroutineScope.() -> Unit) {
        uiScope.launch {
            semaphore.acquire()
            functionToExecute()
            releaseSemaphore()
        }
    }

    protected fun <T> T.onUIThread(block: suspend CoroutineScope.(T) -> Unit) {
        uiScope.launch {
            semaphore.acquire()
            block(this, this@onUIThread)
            releaseSemaphore()
        }
    }

    open fun onVisibilityChanged(visible: Boolean) {
    }

    protected fun updateBaseState(action: (BaseUIState) -> BaseUIState) = baseState.update(action)

    protected fun openBottomSheet(config: UIBottomSheetConfig) {
        launchOnMain {
            mutex.withLock {
                if (!pendingBottomSheetQueue.contains(config))
                    pendingBottomSheetQueue.add(config)

                if (sheetState.value.child?.instance == null) {
                    sheetNavigation.activate(config)
                    updateBaseState {
                        it.copy(bottomSheetIsOpen = true)
                    }
                }
            }
        }
    }

    override fun closeBottomSheet(action: ((BottomSheetChild?) -> Unit)?) {
        bottomSheetCloseAction = action
        updateBaseState { it.copy(bottomSheetIsOpen = false) }
    }

    //do not call this method manually, use closeBottomSheet() instead
    override fun bottomSheetDidClose(child: BottomSheetChild?) {
        sheetNavigation.dismiss()
        bottomSheetCloseAction?.invoke(child)
        bottomSheetCloseAction = null

        if (baseStateData.bottomSheetIsOpen) {
            updateBaseState {
                it.copy(bottomSheetIsOpen = false)
            }
        }
        onBottomSheetClosed(child)

        launchOnMain {
            mutex.withLock {
                if (pendingBottomSheetQueue.isNotEmpty())
                    pendingBottomSheetQueue.deleteFirst() //remove current
                pendingBottomSheetQueue.firstOrNull()
            }?.also { bsConfig ->
                sheetNavigation.activate(bsConfig)
                updateBaseState {
                    it.copy(bottomSheetIsOpen = true)
                }
            }
        }
    }

    fun showProgress(show: Boolean) = progressListener.setProgress(show)

    open fun onBottomSheetClosed(child: BottomSheetChild?) {}

    /** Override in successor to provide callback to any bottom sheet, launched from this view model.
     * The bottom sheet must have view model in order to access the provided callback. */
    open fun bottomSheetCallback(): ParentCallback? = this

    /** Override in successor to provide navigation to any bottom sheet, launched from this view model. */
    open fun bottomSheetNavigation(): StackNavigation<NavigatorConfig>? = context.navigation

    /** Override in successor to create and configure any bottom sheet, launched in this view model. */
    open fun configureBottomSheets(config: UIBottomSheetConfig, context: AppContext): BottomSheetChild =
        bottomSheetChildFactory(config, this::class.simpleName)

    /** Default implementations of lifecycle callbacks.
     * It's not needed to call super in successors as these functions do nothing */
    open fun onCreate() {}
    open fun onDestroy() {}
    open fun onPause() {}
    open fun onResume() {}
    open fun onStart() {}
    open fun onStop() {}

    open fun onBackButtonClicked() {
        setResult<ParentCallback> { goBack() }
    }

    open fun onKeyboardAction(imeAction: ImeAction): Boolean { return true }

    inline fun <reified T> setResult(block: T.() -> Unit) = parentCallbacks.safeCast<T>()?.also(block)

    private fun doOnResume() {
        checkActionAndPreference()

        releaseSemaphore()
    }

    private fun releaseSemaphore() {
        if (semaphore.availablePermits != 1)
            semaphore.release()
    }

    private fun checkActionAndPreference(): Boolean {
        if (baseAction != ACTION_NONE)
            checkIsAllowedWithReplace(baseAction)

        return true
    }

    protected fun checkIsAllowed(action: Int): Boolean {
        return true
    }

    private fun checkIsAllowedWithReplace(action: Int): Boolean {
        return true
    }

    fun onKeyboardShown() {
        updateBaseState {
            it.copy(showKeyboard = false)
        }
    }

    fun showKeyboard() {
        updateBaseState {
            it.copy(showKeyboard = true)
        }
    }

    fun onKeyboardHidden() {
        updateBaseState {
            it.copy(hideKeyboard = false)
        }
    }

    fun hideKeyboard() {
        updateBaseState {
            it.copy(hideKeyboard = true)
        }
    }

    fun clearFocus() {
        updateBaseState {
            it.copy(clearFocus = true)
        }
    }

    fun onFocusCleared() {
        updateBaseState {
            it.copy(clearFocus = false)
        }
    }

    fun commandBlock(showProgress: Boolean = true, hideProgress: Boolean = true, commandBlock: suspend CoroutineScope.() -> Unit): Job {
        if (showProgress)
            showProgress(true)

        return scope
            .launch(block = commandBlock)
            .apply {
                invokeOnCompletion {
                    if (hideProgress)
                        showProgress(false)
                }
        }
    }

    protected fun onBaseError(baseErrorModel : BaseResponseModel){
        when (baseErrorModel.status) {
            STATUS_EXCEPTION                -> showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
            STATUS_NO_INTERNET_CONNECTION   -> showSnackbar(sharedString(SR.strings.no_internet_connection))
        }
    }

    /** @see [Flow.withLifecycle] */
    @OptIn(ExperimentalMystyBagApi::class)
    protected fun <T> Flow<T>.conflatedWithLifecycle(minActiveState: LifecycleThreshold = LifecycleThreshold.RESUMED): Flow<T> = withLifecycle(lifecycle, Channel.CONFLATED, BufferOverflow.SUSPEND, minActiveState)

    /** @see withLifecycle */
    @OptIn(ExperimentalMystyBagApi::class)
    protected fun <T> ReceiveChannel<T>.withLifecycle(minActiveState: LifecycleThreshold = LifecycleThreshold.RESUMED): ReceiveChannel<T> = withLifecycle(lifecycle, scope, minActiveState)
}

class ViewModelCoroutineScope(context: CoroutineContext, coroutineName: String?): CoroutineScope {
    private var onViewDetachJob  = SupervisorJob()
    private val exceptionHandler = CoroutineExceptionHandler { ctx, exception ->
        if (exception !is CancellationException) {
            val message = exception.message.orEmpty()
            val isBenignAbortError =
                PlatformHelper.isWeb && (
                    message.contains("BodyStreamBuffer was aborted", ignoreCase = true) ||
                        message.contains("AbortError", ignoreCase = true)
                    )
            if (isBenignAbortError) {
                // Ktor JS may throw AbortError when a fetch/body stream is cancelled
                // (normal lifecycle cancellation in web composition/navigation).
                return@CoroutineExceptionHandler
            }
            val name = ctx[CoroutineName]?.name.ifNullOrEmpty { "Unknown" }
            if (exception is Exception) {
                logException(UnhandledSharedCodeException(exception.message?.plus(" [$name]") ?: name, exception))
            }
            else if (exception is Error)
                logException(UnhandledSharedCodeException(exception.message?.plus(" [$name]") ?: "Unhandled shared error [$name]", exception))

            logEvent("Uncaught Exception in [$name] -> ${exception.message} -> ${exception.stackTraceToString().take(200)}")
            throw UnhandledSharedCodeException("Unhandled exception caught in shared code coroutine [$name]", exception)
        }
    }
    override val coroutineContext: CoroutineContext = context + CoroutineName(coroutineName ?: "NotSet") + onViewDetachJob + exceptionHandler

    fun cancel() {
        onViewDetachJob.cancel()
    }

    fun cancelChildren() {
        coroutineContext.cancelChildren()
    }
}

class UnhandledSharedCodeException(message: String, cause: Throwable? = null): Exception(message, cause)
class UnhandledCommonCodeException(message: String, cause: Throwable? = null): Exception(message, cause)